import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ModelLogo } from '@shared/brand';
import { idr } from '@shared/format';
import { CopyButton, Icon, setStoredTheme, type IconName } from '../components/ui';
import { MarkdownLite } from './markdown-lite';
import { GROUP_LABELS, looksLikeQuestion, rank, type RankedGroup, type SearchItem } from './search-core';
import type { SearchMode } from './SearchProvider';
import { actionItems, docItems, keyItems, modelItems, pageItems, projectItems, publicPageItems } from './sources';
import { useAssist, useAssistStatus, type AssistState } from './useAssist';
import { useSearchData } from './useSearchData';
import '../styles/search.css';

const RECENT_KEY = 'aiagg.search.recent';
type Stored = Pick<SearchItem, 'id' | 'group' | 'title' | 'subtitle' | 'to' | 'icon' | 'logo'>;

const isPublicPath = (to: string) => to === '/' || to.startsWith('/#') || to.startsWith('/docs');

function readRecent(app: boolean): SearchItem[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as Stored[];
    return raw
      .filter((entry) => entry?.to && (app || isPublicPath(entry.to)))
      .map((entry) => ({ ...entry, id: `recent:${entry.id}`, group: 'recent', text: '' }));
  } catch {
    return [];
  }
}

function remember(item: SearchItem) {
  if (!item.to || item.group === 'ai') return;
  try {
    const id = item.id.replace(/^recent:/, '');
    const raw = (JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as Stored[]).filter((entry) => entry.id !== id);
    const entry: Stored = { id, group: item.group === 'recent' ? 'page' : item.group, title: item.title, subtitle: item.subtitle, to: item.to, icon: item.icon, logo: item.logo };
    localStorage.setItem(RECENT_KEY, JSON.stringify([entry, ...raw].slice(0, 5)));
  } catch {
    // storage blocked
  }
}

const SUGGESTED_DOCS = ['doc:home', 'doc:chat', 'doc:error'];

const EXAMPLE_QUESTIONS = ['Bagaimana cara memakai streaming?', 'Kenapa request saya kena error 429?', 'Bagaimana cara isi saldo lewat QRIS?'];

const touchOnly = () => typeof window !== 'undefined' && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

type Section = RankedGroup & { label: string };

export function CommandPalette({ mode, initialQuery, onClose }: { mode: SearchMode; initialQuery: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const app = mode.kind === 'app';
  const signOut = app ? mode.signOut : undefined;
  const data = useSearchData(app);
  const status = useAssistStatus(app);
  const [spent, setSpent] = useState<number | null>(null);
  const assist = useAssist(useCallback((remaining: number) => setSpent(remaining), []));
  const remaining = spent ?? status?.remaining_today;
  const aiOn = app && !!status?.enabled;
  const aiOut = aiOn && remaining === 0;
  const [query, setQuery] = useState(initialQuery);
  const [active, setActive] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const answering = assist.state.phase !== 'idle';

  const corpus = useMemo(
    () =>
      signOut
        ? [
            ...pageItems(),
            ...actionItems({ signOut, setTheme: setStoredTheme }),
            ...modelItems(data.models, idr),
            ...projectItems(data.projects),
            ...keyItems(data.keys),
            ...docItems(),
          ]
        : [...publicPageItems(), ...docItems()],
    [signOut, data],
  );

  const trimmed = query.trim();

  const sections = useMemo<Section[]>(() => {
    if (!trimmed) {
      const recent = readRecent(app);
      const suggested = app ? corpus.filter((i) => i.group === 'action').slice(0, 4) : corpus.filter((i) => i.group === 'page');
      const docs = corpus.filter((i) => SUGGESTED_DOCS.includes(i.id));
      const examples: SearchItem[] = EXAMPLE_QUESTIONS.map((question, i) => ({ id: `ask:${i}`, group: 'ai', title: question, text: '', icon: 'spark' }));
      return [
        ...(recent.length ? [{ group: 'recent' as const, label: GROUP_LABELS.recent, items: recent }] : []),
        { group: app ? ('action' as const) : ('page' as const), label: 'Saran', items: suggested },
        ...(aiOn && !aiOut ? [{ group: 'ai' as const, label: 'Coba tanya AI', items: examples }] : []),
        { group: 'doc' as const, label: 'Dokumentasi populer', items: docs },
      ];
    }
    const ranked: Section[] = rank(corpus, trimmed).map((g) => ({ ...g, label: GROUP_LABELS[g.group] }));
    const aiItem: SearchItem | null = app
      ? aiOn
        ? {
            id: aiOut ? 'ai-off' : 'ai',
            group: 'ai',
            title: `Tanya AI: “${trimmed}”`,
            subtitle: aiOut ? 'Jatah hari ini sudah habis. Coba lagi besok.' : 'Jawaban singkat dari dokumentasi Partner AI',
            text: '',
            icon: 'spark',
          }
        : null
      : { id: 'ai-login', group: 'ai', title: 'Masuk untuk bertanya ke AI', subtitle: `“${trimmed}”`, text: '', icon: 'spark', to: '/login' };
    if (!aiItem) return ranked;
    const ai: Section = { group: 'ai', label: GROUP_LABELS.ai, items: [aiItem] };
    return !aiOut && (looksLikeQuestion(trimmed) || ranked.length === 0) ? [ai, ...ranked] : [...ranked, ai];
  }, [trimmed, corpus, app, aiOn, aiOut]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const noMatch = !!trimmed && sections.every((s) => s.group === 'ai');

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    inputRef.current?.focus();
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = before;
      opener?.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => setActive(0), [trimmed]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const { reset } = assist;
  const back = useCallback(() => {
    reset();
    inputRef.current?.focus();
  }, [reset]);

  // Esc from anywhere
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (answering) back();
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [answering, back, onClose]);

  const go = (to: string) => {
    // landing is separate page
    if (to === '/' || to.startsWith('/#')) window.location.assign(to);
    else navigate(to);
  };

  const askAi = (question: string) => {
    setQuery(question);
    // hide mobile keyboard
    if (touchOnly()) inputRef.current?.blur();
    void assist.ask(question, pathname);
  };

  const choose = (item: SearchItem | undefined) => {
    if (!item || item.id === 'ai-off') return;
    if (item.id === 'ai') return askAi(trimmed);
    if (item.id.startsWith('ask:')) return askAi(item.title);
    remember(item);
    onClose();
    if (item.run) item.run();
    else if (item.to) go(item.to);
  };

  const onKey = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (answering) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(flat.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      choose(flat[active]);
    }
  };

  // keep Tab inside
  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const nodes = [...dialogRef.current.querySelectorAll<HTMLElement>('input, button:not(:disabled), a[href]')].filter((node) => node.offsetParent !== null);
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const clear = () => {
    setQuery('');
    if (answering) reset();
    inputRef.current?.focus();
  };

  let index = 0;
  return createPortal(
    <div className="cmdk-backdrop" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Cari atau tanya"
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={trapFocus}
      >
        <div className="cmdk-field">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              if (answering) reset();
            }}
            onKeyDown={onKey}
            placeholder={app ? 'Cari atau tanya apa saja…' : 'Cari dokumentasi…'}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={!answering && flat[active] ? `cmdk-${active}` : undefined}
            aria-autocomplete="list"
            enterKeyHint="go"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {query && (
            <button type="button" className="cmdk-clear" onClick={clear} aria-label="Hapus teks">
              <Icon name="close" size={14} />
            </button>
          )}
          <button type="button" className="cmdk-close" onClick={onClose} aria-label="Tutup pencarian">
            <span className="cmdk-close-key">Esc</span>
            <span className="cmdk-close-text">Tutup</span>
          </button>
        </div>

        {assist.state.phase !== 'idle' ? (
          <Answer state={assist.state} onBack={back} onRetry={() => void assist.ask(assist.state.phase === 'idle' ? trimmed : assist.state.question, pathname)} onNavigate={onClose} />
        ) : flat.length === 0 ? (
          <div className="cmdk-empty">
            <Icon name="search" size={22} />
            <p>
              Tidak ada hasil untuk <b>“{trimmed}”</b>.
            </p>
            <span>Coba kata lain, misalnya streaming, 429, atau nama model.</span>
          </div>
        ) : (
          <div className="cmdk-list" ref={listRef}>
            {noMatch && (
              <p className="cmdk-none">
                Tidak ada hasil untuk <b>“{trimmed}”</b>.{aiOn && !aiOut ? ' Coba tanyakan ke AI:' : ''}
              </p>
            )}
            <div id="cmdk-list" role="listbox" aria-label="Hasil">
              {sections.map((section) => (
                <div key={section.group} role="group" aria-label={section.label}>
                  <div className="cmdk-label">{section.label}</div>
                  {section.items.map((item) => {
                    const i = index++;
                    const off = item.id === 'ai-off';
                    return (
                      <div
                        key={item.id}
                        id={`cmdk-${i}`}
                        data-index={i}
                        role="option"
                        aria-selected={i === active}
                        aria-disabled={off || undefined}
                        className={`cmdk-item${i === active ? ' active' : ''}${item.group === 'ai' ? ' ai' : ''}${off ? ' off' : ''}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseMove={() => setActive(i)}
                        onClick={() => choose(item)}
                      >
                        <span className="cmdk-icon">
                          {item.logo ? (
                            <ModelLogo model={item.logo.model} provider={item.logo.provider} family={item.logo.family} size={20} />
                          ) : (
                            <Icon name={(item.icon ?? 'arrow') as IconName} size={16} />
                          )}
                        </span>
                        <span className="cmdk-text">
                          <span className="cmdk-title">{item.title}</span>
                          {item.subtitle && <span className="cmdk-sub">{item.subtitle}</span>}
                        </span>
                        {!off && <span className="cmdk-go">{item.group === 'ai' && !item.to ? 'Tanya' : <Icon name="arrow" size={15} />}</span>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="cmdk-foot">
          {!answering && (
            <>
              <span className="cmdk-hint" aria-hidden>
                <kbd>↑</kbd>
                <kbd>↓</kbd> pilih
              </span>
              <span className="cmdk-hint" aria-hidden>
                <kbd>Enter</kbd> buka
              </span>
            </>
          )}
          <span className="cmdk-hint" aria-hidden>
            <kbd>Esc</kbd> {answering ? 'kembali' : 'tutup'}
          </span>
          {aiOn && remaining !== undefined && (
            <span className={`cmdk-quota${aiOut ? ' out' : ''}`}>
              <Icon name="spark" size={13} />
              {aiOut ? 'Jatah Tanya AI hari ini habis' : `Tanya AI: sisa ${remaining} hari ini`}
            </span>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Answer({
  state,
  onBack,
  onRetry,
  onNavigate,
}: {
  state: Exclude<AssistState, { phase: 'idle' }>;
  onBack: () => void;
  onRetry: () => void;
  onNavigate: () => void;
}) {
  return (
    <div className="cmdk-answer">
      <div className="cmdk-answer-scroll" aria-live="polite" aria-busy={state.phase === 'loading'}>
        <div className="cmdk-answer-head">
          <span className="cmdk-answer-badge">
            <Icon name="spark" size={14} />
            Tanya AI
          </span>
          <span className="cmdk-answer-q">{state.question}</span>
        </div>
        {state.phase === 'loading' && (
          <div className="cmdk-answer-loading" role="status">
            <span className="cmdk-answer-status">
              <span className="spinner" />
              Mencari jawaban di dokumentasi…
            </span>
            <span className="cmdk-skel" style={{ width: '92%' }} />
            <span className="cmdk-skel" style={{ width: '78%' }} />
            <span className="cmdk-skel" style={{ width: '85%' }} />
            <span className="cmdk-skel" style={{ width: '46%' }} />
            <span className="cmdk-slow">Masih memproses, sebentar lagi…</span>
          </div>
        )}
        {state.phase === 'error' && (
          <div className="cmdk-answer-error" role="alert">
            <Icon name="warn" size={18} />
            <p>{state.message}</p>
          </div>
        )}
        {state.phase === 'done' && (
          <>
            <div className="cmdk-answer-body">
              <MarkdownLite text={state.result.answer} onNavigate={onNavigate} />
            </div>
            {state.result.sources.length > 0 && (
              <div className="cmdk-sources">
                <span>Sumber</span>
                {state.result.sources.map((source) => (
                  <Link key={source.url} to={source.url} onClick={onNavigate}>
                    <Icon name="book" size={13} />
                    {source.title}
                  </Link>
                ))}
              </div>
            )}
            <p className="cmdk-disclaimer">Jawaban AI bisa keliru. Cek dokumentasi untuk detail.</p>
          </>
        )}
      </div>
      <div className="cmdk-answer-actions">
        <button type="button" className="btn btn-sm btn-ghost cmdk-back" onClick={onBack}>
          <Icon name="left" size={14} />
          {state.phase === 'error' && !state.retry ? 'Lihat hasil pencarian' : 'Kembali ke hasil'}
        </button>
        {state.phase === 'done' && <CopyButton value={state.result.answer} label="Salin jawaban" className="cmdk-copy" />}
        {state.phase === 'error' && state.retry && (
          <button type="button" className="btn btn-sm btn-primary" onClick={onRetry}>
            Coba lagi
          </button>
        )}
      </div>
    </div>
  );
}
